import { evaluateDrinkingWindow, windowYearsAreCoherent, } from "./drinking-window.js";
/** Walked in this order, and the page is laid out in it. */
export const URGENCY_ORDER = [
    "past",
    "closing",
    "peak",
    "soon",
    "opens",
];
/**
 * How far ahead *soon* reaches.
 *
 * Three years, chosen against this cellar's actual distribution rather than
 * picked as a round number: the `drinkBy` years cluster at 2028 and again at
 * 2033–2034, so one year names two wines, three years names about twenty, and
 * five would name nearly half the cellar. A list of work stops being read at
 * either end of that. The reasoning is in `project-scope.md`, so moving it is
 * a decision and not a tweak.
 */
export const SOON_HORIZON_YEARS = 3;
/**
 * Why this bottle is being asked for, or null for one that is not.
 *
 * Null covers three different quiet cases and deliberately does not
 * distinguish them here: a wine with no years at all, a wine whose years
 * contradict each other, and a wine whose window is simply years away. The
 * first two are what *Luckor* and the suggestion button are for, and the
 * screen counts them at the foot of the page rather than listing them.
 */
export function drinkingUrgency(years, currentYear) {
    if (!windowYearsAreCoherent(years))
        return null;
    if (years.drinkBy !== null) {
        if (years.drinkBy < currentYear)
            return "past";
        if (years.drinkBy === currentYear)
            return "closing";
    }
    // Asked of the shared evaluation rather than re-derived from `peakFrom` and
    // `peakTo`, so that what this page calls a peak is the same thing the band
    // above the row draws and the filter on the wine list narrows by.
    if (evaluateDrinkingWindow(years, currentYear) === "peak")
        return "peak";
    if (years.drinkBy !== null && years.drinkBy - currentYear <= SOON_HORIZON_YEARS) {
        return "soon";
    }
    // Exactly this year, not *any time from now on*: a wine that became drinkable
    // four years ago is not news, and it would otherwise stand here for the rest
    // of its life under a heading announcing it had just arrived.
    if (years.drinkFrom === currentYear)
        return "opens";
    return null;
}
/**
 * Whether everything this row is being asked for rests on the single year the
 * spreadsheet gave it.
 *
 * It is the weakest thing the cellar knows about a wine: one number carried
 * out of one column, with no reasoning behind it, no near end to the window at
 * all, and — on the evidence of the sheet — frequently a round figure someone
 * wrote once and never revisited. It is also the state 83 of the 106 owned
 * wines are in, so a view that did not say so would consist almost entirely of
 * numbers
 * presented as judgements. That is the misreading the provenance columns were
 * added to prevent, arriving through a different door.
 *
 * Deliberately narrow: one year, that year is `drinkBy`, and it came from the
 * import. A wine whose imported year has been weighed and added to — a peak
 * accepted from a suggestion, a `drinkFrom` typed by hand — has been judged by
 * somebody, and the caveat would be false of it.
 */
export function restsOnTheImport(years, sources) {
    return (years.drinkBy !== null &&
        years.drinkFrom === null &&
        years.peakFrom === null &&
        years.peakTo === null &&
        sources.drinkBySource === "Import");
}
/**
 * How near a bottle is to the thing that put it on the list — the sort within
 * a group, and the reason the answer is a number rather than a comparator.
 *
 * Smaller is more urgent, and the scale is years so that the number means
 * something when it is read: a window closed three years ago sorts as -3, one
 * closing this year as 0, one closing in two years as 2. A peak is measured by
 * the end of the plateau, because what is being lost is the far edge of it and
 * not the near one; a plateau with no end recorded sorts last within its
 * group, since there is nothing about it that is running out.
 */
export function yearsOfGrace(years, urgency, currentYear) {
    if (urgency === "peak") {
        return years.peakTo === null ? Number.MAX_SAFE_INTEGER : years.peakTo - currentYear;
    }
    if (urgency === "opens")
        return 0;
    return years.drinkBy === null ? Number.MAX_SAFE_INTEGER : years.drinkBy - currentYear;
}
/**
 * The list, grouped and sorted: every wine that is in a hurry, under the
 * heading that says why, nearest to its deadline first.
 *
 * Generic over the row, because the two things that need it hold different
 * ones — the screen has the list contract, the monthly report has the archive
 * — and what they share is the four years, the four provenances and the
 * number on the label. Written out as a constraint rather than taken as
 * `WineListRow` for that reason.
 *
 * **Ownership is not decided here.** Whether a wine still has bottles is asked
 * of a different shape on each side, so each caller filters before calling.
 * What must not differ between them is the grouping, the order and the caveat,
 * and those are here — task `8.1` recorded that a second implementation of
 * *is this bottle in a hurry* would drift in the one direction that matters,
 * and `8.8` is where the second reader arrived.
 *
 * Sorted by years of grace and then by the wine number, so two bottles closing
 * in the same year come in the order they stand on the shelf rather than in
 * whatever order they were handed over.
 */
export function groupByUrgency(wines, currentYear) {
    const groups = new Map();
    for (const wine of wines) {
        const urgency = drinkingUrgency(wine, currentYear);
        if (urgency === null)
            continue;
        const listed = groups.get(urgency) ?? [];
        // Twice, because a row is both the four years and the four provenances —
        // the contract pairs them for exactly this reason.
        listed.push({ wine, onImport: restsOnTheImport(wine, wine) });
        groups.set(urgency, listed);
    }
    return URGENCY_ORDER.flatMap((urgency) => {
        const listed = groups.get(urgency);
        if (listed === undefined || listed.length === 0)
            return [];
        listed.sort((a, b) => yearsOfGrace(a.wine, urgency, currentYear) -
            yearsOfGrace(b.wine, urgency, currentYear) || a.wine.number - b.wine.number);
        return [{ urgency, wines: listed, allOnImport: listed.every((row) => row.onImport) }];
    });
}
//# sourceMappingURL=drinking-urgency.js.map